import { useState } from 'react';

import { InstallInstructions } from './InstallInstructions.tsx';
import { dismissInstallOffer, promptToInstall } from './installStore.ts';
import { useInstallOffer } from './useInstallOffer.ts';

/**
 * Offers to put the app on the home screen. Renders nothing unless it can be.
 *
 * The app has been installable since the service worker landed; nothing said so. Chrome's
 * own infobar is a strip that appears once and is easy to swipe away, and Safari never
 * offers anything at all — on the platform where the app most wants to be an icon, the
 * only route is a Share sheet the person has to already know about. So this says it out
 * loud, once, in the page.
 *
 * In flow on the home screen rather than floating over it: the update prompt already owns
 * the fixed position at the top, and two overlapping notices is worse than one row of list
 * pushed down. `HomePage` renders it whatever state the screen is in — whether it can be
 * offered at all is the store's decision and nobody else's, because we have already taken
 * the browser's own offer away by the time anything here is rendered.
 */
export function InstallBanner() {
  const method = useInstallOffer();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  if (!method) {
    return null;
  }

  return (
    <section className="card install-banner">
      <p className="install-banner__text">
        Add Listocial to your home screen. It opens full screen, straight into the list, and
        keeps working where the signal does not.
      </p>

      <div className="install-banner__actions">
        <button
          type="button"
          className="button button--primary button--small"
          onClick={() => {
            if (method === 'prompt') {
              void promptToInstall();
            } else {
              setIsHelpOpen(true);
            }
          }}
        >
          Add to home screen
        </button>

        <button
          type="button"
          className="button button--quiet button--small"
          onClick={dismissInstallOffer}
        >
          Not now
        </button>
      </div>

      {isHelpOpen && (
        <InstallInstructions
          // Closing counts as having been told. Safari gives a page no way to know whether
          // the icon was added, so the alternative is asking again for ever — including of
          // the person who has already done it and is only in Safari by accident.
          onClose={() => {
            setIsHelpOpen(false);
            dismissInstallOffer();
          }}
        />
      )}
    </section>
  );
}
